import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PAYMENT_METHODS,
  PLATFORM_BILLING_MODELS,
  PLATFORM_FREQUENCIES,
  PLATFORM_INVOICE_TYPES,
  PLATFORM_LICENSING,
  type Platform,
  type PlatformBillingModel,
  billingModelOf,
  linesToUsage,
  usageToLines,
} from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage, savePlatform } from "../services/expensesService";
import { Dialog, ErrorBox, Field } from "./ui";
import { categoryOptions, money } from "../lib/format";
import { PLATFORM_KINDS, categoryForPlatformKind, platformKindOf, type PlatformKind } from "@torre/expenses";
import { ui } from "../theme";
import { PLATFORMS_NS } from "../locales/platformsI18n";

// Alta y edición de una plataforma con su modelo de cobro (#956).

type Form = {
  name: string;
  vendor: string;
  aliases: string;
  category: string;
  billingModel: PlatformBillingModel;
  frequency: string;
  expectedAmount: string;
  seats: string;
  seatPrice: string;
  seatHolders: string;
  consumptionUnit: string;
  spikeThreshold: string;
  currency: string;
  paymentMethod: string;
  cardLast4: string;
  paymentNotes: string;
  owner: string;
  billingDay: string;
  nextRenewal: string;
  url: string;
  active: boolean;
  notes: string;
  usedBy: string;
  tenants: string;
  shared: boolean;
  licensing: string;
  invoiceType: string;
  paidBy: string;
};

const numText = (v: number | null | undefined) => (v != null ? String(v) : "");

const toForm = (p?: Platform | null): Form => ({
  name: p?.name || "",
  vendor: p?.vendor || "",
  aliases: (p?.aliases || []).join(", "),
  category: p?.category || "plataformas",
  billingModel: p ? billingModelOf(p) : "servicio",
  frequency: p?.frequency || "mensual",
  expectedAmount: numText(p?.expectedAmount),
  seats: numText(p?.seats),
  seatPrice: numText(p?.seatPrice),
  seatHolders: (p?.seatHolders || []).map((h) => [h.name, h.email].filter(Boolean).join(", ")).join("\n"),
  consumptionUnit: p?.consumptionUnit || "",
  spikeThreshold: p?.spikeThreshold != null ? String(Math.round(p.spikeThreshold * 100)) : "",
  currency: p?.currency || "MXN",
  paymentMethod: p?.paymentMethod || "tarjeta_credito",
  cardLast4: p?.cardLast4 || "",
  paymentNotes: p?.paymentNotes || "",
  owner: p?.owner || "",
  billingDay: numText(p?.billingDay),
  nextRenewal: p?.nextRenewal || "",
  url: p?.url || "",
  active: p?.active !== false,
  notes: p?.notes || "",
  usedBy: usageToLines(p?.usedBy),
  tenants: (p?.tenants || []).join(", "),
  shared: p?.shared === true,
  licensing: p?.licensing || "",
  invoiceType: p?.invoiceType || "",
  paidBy: p?.paidBy || "",
});

const num = (s: string) => (s.trim() === "" ? null : Number(s));

/** Cuerpo que se manda a la API a partir del formulario. */
function formToPayload(form: Form, previous?: Platform | null): Partial<Platform> {
  const model = form.billingModel;
  const holders = form.seatHolders
    .split("\n")
    .map((line) => line.split(",").map((x) => x.trim()))
    .filter(([name, email]) => name || email)
    .map(([name, email]) => ({ name: name || "", email: email || null }));
  return {
    name: form.name,
    vendor: form.vendor || null,
    aliases: form.aliases.split(",").map((a) => a.trim()).filter(Boolean),
    category: form.category as Platform["category"],
    billingModel: model,
    frequency: form.frequency as Platform["frequency"],
    expectedAmount: model === "asiento" ? undefined : num(form.expectedAmount),
    seats: model === "asiento" ? num(form.seats) : null,
    seatPrice: model === "asiento" ? num(form.seatPrice) : null,
    seatHolders: model === "asiento" ? holders : [],
    consumptionUnit: model === "consumo" ? form.consumptionUnit || null : null,
    spikeThreshold: model === "consumo" && form.spikeThreshold !== "" ? Number(form.spikeThreshold) / 100 : null,
    currency: form.currency,
    paymentMethod: form.paymentMethod as Platform["paymentMethod"],
    cardLast4: form.cardLast4 || null,
    paymentNotes: form.paymentNotes.trim() || null,
    owner: form.owner || null,
    billingDay: num(form.billingDay),
    nextRenewal: form.nextRenewal || null,
    url: form.url || null,
    active: form.active,
    notes: form.notes || null,
    usedBy: linesToUsage(form.usedBy, previous?.usedBy || []),
    tenants: form.tenants.split(",").map((x) => x.trim()).filter(Boolean),
    shared: form.shared,
    licensing: (form.licensing || null) as Platform["licensing"],
    invoiceType: (form.invoiceType || null) as Platform["invoiceType"],
    paidBy: form.paidBy || null,
  };
}

export default function PlatformDialog({
  open,
  platform,
  onClose,
  onSaved,
}: {
  open: boolean;
  platform: Platform | null;
  onClose: () => void;
  onSaved: (saved: Platform & { linkedExpenses?: number; recategorizedExpenses?: number }) => void;
}) {
  const { t, i18n } = useTranslation("common");
  const { t: tp } = useTranslation(PLATFORMS_NS);
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const [form, setForm] = useState<Form>(toForm(platform));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(toForm(platform));
      setError(null);
    }
  }, [open, platform]);

  const set = (k: keyof Form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  // El tipo (plataforma / infraestructura) es la categoría: elegirlo la cambia, y al revés.
  const kind = platformKindOf({ category: form.category as Platform["category"] });
  const setKind = (k: PlatformKind) => setForm((f) => ({ ...f, category: categoryForPlatformKind(k, f.category as Platform["category"]) }));
  const seatCost = form.seats !== "" && form.seatPrice !== "" ? Number(form.seats) * Number(form.seatPrice) : null;
  const invalid =
    !form.name.trim() || (form.billingModel === "asiento" && (form.seats === "" || form.seatPrice === ""));

  const submit = async () => {
    setSaving(true);
    try {
      const saved = await savePlatform(formToPayload(form, platform), platform?.id);
      onSaved(saved);
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      title={platform ? tp("edit") : tp("add")}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={c.secondary} onClick={onClose}>
            {t("expenses.cancel")}
          </button>
          <button type="button" className={c.primary} disabled={invalid || saving} onClick={submit}>
            {saving ? t("expenses.saving") : t("expenses.save")}
          </button>
        </>
      }
    >
      <ErrorBox message={error} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label={tp("form.name")}>
          <input className={c.input} value={form.name} onChange={set("name")} placeholder="Google Workspace" />
        </Field>
        <Field label={tp("form.vendor")}>
          <input className={c.input} value={form.vendor} onChange={set("vendor")} placeholder="Proveedor Ficticio, S.A. de C.V." />
        </Field>
        <Field label={tp("kind.field")} className="sm:col-span-2">
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={tp("kind.field")}>
            {PLATFORM_KINDS.map((k) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={kind === k}
                onClick={() => setKind(k)}
                className={`rounded-lg border px-3 py-2 text-left text-sm ${kind === k ? "border-teal-500 bg-teal-500/10" : c.border} ${c.text}`}
              >
                <span className="block font-medium">{tp(`kind.${k}`)}</span>
                <span className={`block text-xs font-normal ${c.muted}`}>{tp(`kind.${k}Hint`)}</span>
              </button>
            ))}
          </div>
        </Field>
        <Field label={tp("form.aliases")} className="sm:col-span-2">
          <input className={c.input} value={form.aliases} onChange={set("aliases")} placeholder="GOOGLE *GSUITE, GOOGLE WORKSPACE" />
          <span className="mt-1 block font-normal">{tp("form.aliasesHint")}</span>
        </Field>

        <fieldset className="sm:col-span-2">
          <legend className={`mb-1 text-xs font-medium ${c.muted}`}>{tp("model.label")}</legend>
          <div role="radiogroup" className="flex flex-wrap gap-2">
            {PLATFORM_BILLING_MODELS.map((m) => (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={form.billingModel === m}
                onClick={() => setForm((f) => ({ ...f, billingModel: m }))}
                className={`rounded-lg border px-3 py-1.5 text-sm ${form.billingModel === m ? "border-teal-600 bg-teal-600/10 text-teal-700 dark:text-teal-300" : `${c.border} ${c.muted}`}`}
              >
                {tp(`model.${m}`)}
              </button>
            ))}
          </div>
          <p className={`mt-1 text-xs ${c.muted}`}>{tp(`model.hint_${form.billingModel}`)}</p>
        </fieldset>

        <Field label={tp("form.frequency")}>
          <select className={c.input} value={form.frequency} onChange={set("frequency")}>
            {PLATFORM_FREQUENCIES.map((f) => (
              <option key={f} value={f}>
                {t(`expenses.frequency.${f}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("expenses.fields.currency")}>
          <select className={c.input} value={form.currency} onChange={set("currency")}>
            {["MXN", "USD", "EUR"].map((x) => (
              <option key={x}>{x}</option>
            ))}
          </select>
        </Field>

        {form.billingModel === "servicio" && (
          <Field label={tp("form.fee")}>
            <input type="number" min="0" step="0.01" className={c.input} value={form.expectedAmount} onChange={set("expectedAmount")} />
          </Field>
        )}
        {form.billingModel === "asiento" && (
          <>
            <Field label={tp("form.seats")}>
              <input type="number" min="0" step="1" className={c.input} value={form.seats} onChange={set("seats")} />
            </Field>
            <Field label={tp("form.seatPrice")}>
              <input type="number" min="0" step="0.01" className={c.input} value={form.seatPrice} onChange={set("seatPrice")} />
              {seatCost != null && Number.isFinite(seatCost) && (
                <span className="mt-1 block font-normal">{tp("form.seatCost", { value: money(seatCost, form.currency, i18n.language) })}</span>
              )}
            </Field>
            <Field label={tp("form.seatHolders")} className="sm:col-span-2">
              <textarea rows={3} className={c.input} value={form.seatHolders} onChange={set("seatHolders")} placeholder={"Ana Ejemplo, ana@example.test\nBeto Ejemplo"} />
              <span className="mt-1 block font-normal">{tp("form.seatHoldersHint")}</span>
            </Field>
          </>
        )}
        {form.billingModel === "consumo" && (
          <>
            <Field label={tp("form.unit")}>
              <input className={c.input} value={form.consumptionUnit} onChange={set("consumptionUnit")} placeholder={tp("form.unitPlaceholder")} list="expenses-consumption-units" />
              <datalist id="expenses-consumption-units">
                {["tokens", "GB", "minutos", "solicitudes", "horas", "mensajes", "créditos"].map((u) => (
                  <option key={u} value={u} />
                ))}
              </datalist>
            </Field>
            <Field label={tp("form.estimate")}>
              <input type="number" min="0" step="0.01" className={c.input} value={form.expectedAmount} onChange={set("expectedAmount")} />
            </Field>
            <Field label={tp("form.threshold")} className="sm:col-span-2">
              <input type="number" min="1" max="1000" className={c.input} value={form.spikeThreshold} onChange={set("spikeThreshold")} placeholder="25" />
              <span className="mt-1 block font-normal">{tp("form.thresholdHint")}</span>
            </Field>
          </>
        )}

        <Field label={t("expenses.fields.category")}>
          <select className={c.input} value={form.category} onChange={set("category")}>
            {categoryOptions(i18n.language, { include: form.category }).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("expenses.fields.paymentMethod")}>
          <select className={c.input} value={form.paymentMethod} onChange={set("paymentMethod")}>
            {PAYMENT_METHODS.map((m) => (
              <option key={m} value={m}>
                {t(`expenses.payment.${m}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("expenses.fields.card")}>
          <input className={c.input} maxLength={4} value={form.cardLast4} onChange={set("cardLast4")} />
        </Field>
        <Field label={tp("form.paymentNotes")} className="sm:col-span-2">
          <textarea rows={2} className={c.input} value={form.paymentNotes} onChange={set("paymentNotes")} placeholder={tp("form.paymentNotesPlaceholder")} />
        </Field>
        <Field label={t("expenses.platforms.owner")}>
          <input className={c.input} value={form.owner} onChange={set("owner")} />
        </Field>
        <Field label={t("expenses.platforms.billingDay")}>
          <input type="number" min="1" max="31" className={c.input} value={form.billingDay} onChange={set("billingDay")} />
        </Field>
        <Field label={tp("form.nextRenewal")}>
          <input type="date" className={c.input} value={form.nextRenewal} onChange={set("nextRenewal")} />
          <span className="mt-1 block font-normal">{tp("form.nextRenewalHint")}</span>
        </Field>
        <Field label="URL" className="sm:col-span-2">
          <input className={c.input} value={form.url} onChange={set("url")} placeholder="https://example.test/billing" />
        </Field>
        <p className={`text-xs font-medium sm:col-span-2 ${c.muted}`}>{tp("diligence.title")}</p>
        <Field label={tp("diligence.usedBy")} className="sm:col-span-2">
          <textarea rows={3} className={c.input} value={form.usedBy} onChange={set("usedBy")} placeholder={"CC-CONSOLE: apps/v5, functions\nWAYAK: functions"} />
          <span className="mt-1 block font-normal">{tp("diligence.usedByHint")}</span>
        </Field>
        <Field label={tp("diligence.tenants")}>
          <input className={c.input} value={form.tenants} onChange={set("tenants")} placeholder="coding-cloud, wayak, dumax" />
        </Field>
        <Field label={tp("diligence.paidBy")}>
          <input className={c.input} value={form.paidBy} onChange={set("paidBy")} placeholder="Coding Cloud" />
        </Field>
        <Field label={tp("diligence.licensing")}>
          <select className={c.input} value={form.licensing} onChange={set("licensing")}>
            <option value="">—</option>
            {PLATFORM_LICENSING.map((l) => (
              <option key={l} value={l}>
                {tp(`licensing.${l}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={tp("diligence.invoiceType")}>
          <select className={c.input} value={form.invoiceType} onChange={set("invoiceType")}>
            <option value="">—</option>
            {PLATFORM_INVOICE_TYPES.map((x) => (
              <option key={x} value={x}>
                {tp(`invoiceType.${x}`)}
              </option>
            ))}
          </select>
        </Field>
        <label className={`flex items-center gap-2 text-sm sm:col-span-2 ${c.muted}`}>
          <input type="checkbox" className="h-4 w-4 accent-teal-600" checked={form.shared} onChange={(e) => setForm({ ...form, shared: e.target.checked })} />
          {tp("diligence.shared")}
        </label>
        <Field label={t("expenses.fields.notes")} className="sm:col-span-2">
          <textarea rows={3} className={c.input} value={form.notes} onChange={set("notes")} />
        </Field>
        <label className={`flex items-center gap-2 text-sm ${c.muted}`}>
          <input type="checkbox" className="h-4 w-4 accent-teal-600" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
          {t("expenses.platforms.isActive")}
        </label>
      </div>
    </Dialog>
  );
}
